export const USER_ROLES = ['Member', 'Admin', 'Owner', 'Super Admin'] as const;
export const USER_STATUSES = ['Unverified', 'Active', 'Prospect', 'Invited', 'Blocked', 'Banned', 'Deleted'] as const;

/**
 * The colours a person's initials badge can take, in spectrum order (see
 * helpers/user-identity.ts for the hues). Red, orange and amber are left out on purpose,
 * so a badge beside the notification bell never reads as an alert.
 */
export const AVATAR_COLOURS = [
  'olive',
  'green',
  'emerald',
  'teal',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'pink',
] as const;

export const COMPANY_SIZES = ['1-10', '11-30', '31-50', '50+'] as const;
export const COMPANY_TYPES = ['Client Account', 'Agency'] as const;

export const EXPERIMENT_TYPES = ['Multi Variant', 'Server Side', 'URL Redirect', 'Feature Flag'] as const;

/**
 * The types the experiment features (Library, create dialog, Monitor, Program, Learnings)
 * deal in. A 'Feature Flag' row is stored in the same table but is its own feature, under
 * Experiments, Feature flags (P-PER-59), and is kept out of all of those.
 */
export const TEST_EXPERIMENT_TYPES = ['Multi Variant', 'Server Side', 'URL Redirect'] as const;

/**
 * What the UI calls each experiment type. The stored value stays as it is; only the label
 * changes. 'Server Side' is shown as "Server-side" (decided 2026-10-26, after "Code" for a
 * while): the customer's own code shows each variant, and "Code" did not set it apart, since
 * a Multi Variant experiment carries code too. Its Assignment setting says whether the
 * browser or the customer's server picks the variant. 'Feature Flag' is the separate flags
 * feature (feature-flags.ts). Render a type through this, never raw.
 */
export const EXPERIMENT_TYPE_LABELS: Record<(typeof EXPERIMENT_TYPES)[number], string> = {
  'Multi Variant': 'Multi Variant',
  'Server Side': 'Server-side',
  'URL Redirect': 'URL Redirect',
  'Feature Flag': 'Feature flag',
};

/** The label for a type read from a row, which TypeScript only knows as a string. */
export const experimentTypeLabel = (type: string | null | undefined): string =>
  (type && EXPERIMENT_TYPE_LABELS[type as (typeof EXPERIMENT_TYPES)[number]]) || type || '';

/**
 * QA is appended rather than placed beside Draft: the PostgreSQL enum is built from this
 * array, and appending keeps `db:push` to a plain ADD VALUE. Screens that list statuses
 * order them themselves.
 */
export const EXPERIMENT_STATUSES = ['Running', 'Deployed', 'Draft', 'Ended', 'QA'] as const;

/**
 * The statuses before an experiment first goes live. QA is a Draft that can be previewed on
 * the site through a link: it is never shown to a visitor and nothing about it is tracked, so
 * everything that edits, plans, schedules or starts a Draft treats QA the same way.
 */
export const PRE_START_STATUSES = ['Draft', 'QA'] as const;

/** How long QA stays on before the scheduler returns the experiment to Draft. */
export const QA_DURATION_MS = 3 * 24 * 60 * 60 * 1000;

// Server-decided experiments (PER-132). The schema lands first; nothing reads these
// columns until the evaluator, the SDK config and the runtime seed ship.

/**
 * Who assigns a Server Side experiment's variant. 'browser' is today's behaviour: the
 * runtime picks by weight. 'server': the customer's backend asks the SDK, and the browser
 * only adopts the seed it is handed.
 */
export const EXPERIMENT_DECIDED_BY = ['browser', 'server'] as const;

/** What a server-decided experiment hashes on to pick a variant. */
export const EXPERIMENT_BUCKET_ON = ['visitor', 'customer_id'] as const;

/** Operators an attribute rule may use. Not a DB enum: rules are JSON, validated in code. */
export const ATTRIBUTE_RULE_OPERATORS = ['equals', 'in', 'not in', '<', '>', 'exists'] as const;

/**
 * One rule of a server-decided experiment's attribute targeting (experiments.attribute_targeting),
 * matched against the attributes the customer's backend passes to the SDK. Declared here, like
 * SavedTargetingRules, so the schema column, the API and the evaluator share one shape.
 */
export type AttributeRule = {
  /** 1 to 64 characters. */
  attribute: string;
  operator: (typeof ATTRIBUTE_RULE_OPERATORS)[number];
  /** Absent for 'exists'; an array for 'in' and 'not in'. */
  value?: string | number | boolean | Array<string | number>;
};

/**
 * The newest shape version of the config a server SDK downloads. Bumped only on a breaking
 * change: 2 added feature-flag rollouts, which a version 1 reader would split by weight instead.
 * A config without rollouts is still written as version 1 (flag-evaluator's configVersionFor).
 */
export const SDK_CONFIG_VERSION = 2;

/** A flag key, which a server SDK asks for by name: lowercase, digits and hyphens, at most 64. */
export const FLAG_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** The largest variant payload, in bytes of its JSON, that the API accepts. */
export const MAX_VARIANT_PAYLOAD_BYTES = 8 * 1024;

/**
 * The devices a visitor can actually be on, as detectDevice reports them and the tracking
 * server stores them. DEVICE_TYPES adds 'All', which is a targeting choice, not a device.
 */
export const TRACKED_DEVICE_TYPES = ['Desktop', 'Mobile', 'Tablet'] as const;
export const DEVICE_TYPES = ['All', ...TRACKED_DEVICE_TYPES] as const;

/**
 * The predefined expected mechanisms a brief can select, by id. Their labels and descriptions are
 * the dashboard's (state/experiments/expected-mechanisms.ts, whose test keeps the two in step); the
 * server reads only the ids, to keep a stored list to these.
 */
export const EXPECTED_MECHANISM_IDS = ['attention', 'clarity', 'cognitive', 'friction', 'value'] as const;

/**
 * What an activation rule looks for on the customer's site: a named event the site sent, or a CSS
 * selector present in the DOM.
 */
export const ACTIVATION_KINDS = ['event', 'selector'] as const;

/**
 * A pipeline is an ordered run of stages; a stage is a set of experiments that run at
 * the same time. Its own status rather than a reuse of EXPERIMENT_STATUSES: a pipeline
 * is never Deployed, and Paused means "stop advancing", which no experiment status
 * expresses.
 */
export const PIPELINE_STATUSES = ['Draft', 'Running', 'Paused', 'Complete', 'Cancelled'] as const;

export const PIPELINE_STAGE_STATES = ['pending', 'running', 'complete', 'skipped'] as const;

/**
 * When a stage is finished and the next one may be armed.
 *
 * 'all-concluded'  every experiment in the stage has stopped. The default, and the
 *                  only one that cannot end a stage while something is still learning.
 * 'any-significant' the first conclusive result ends the stage and stops the rest.
 *                  Faster, at the cost of abandoning experiments mid-flight.
 * 'manual'         a person decides. For stages whose next step depends on what the
 *                  result was, not merely that there was one.
 */
export const PIPELINE_ADVANCE_RULES = ['all-concluded', 'any-significant', 'manual'] as const;

/**
 * Where a schedule is in its own lifecycle, which is not the experiment's status.
 *
 * armed     waiting for startAt
 * held      startAt has passed, but starting now would collide with something
 *           already running. Re-evaluated every tick; fires when it clears.
 * active    started, waiting for a stop condition
 * complete  stopped, for whatever reason
 * blocked   needs a human. Failed validation at fire time, or the experiment is
 *           reporting a trust failure the engine refuses to act through.
 * cancelled withdrawn by a person
 *
 * held and blocked are separate because they need different responses: held
 * clears itself, blocked never does.
 */
export const SCHEDULE_STATES = ['armed', 'held', 'active', 'complete', 'blocked', 'cancelled'] as const;

/**
 * What stopping an experiment should do.
 *
 * 'end' is the only value the API accepts today. Rolling a winner out to 100% of
 * traffic automatically is opt-in per pipeline, and pipelines do not exist yet, so
 * the other two are defined and rejected at the boundary rather than left as a
 * dormant code path nobody has run.
 */
export const SCHEDULE_CONCLUDE_ACTIONS = ['end', 'deploy-winner', 'auto'] as const;

/**
 * The after action report: what a person says happened once an experiment concluded.
 *
 * Every answer is optional. A report with nothing filled in is valid, so the wizard can be
 * stepped through or skipped without ever being an error. REPORT_QUESTIONS_VERSION is
 * stored with each report so the question set can change later without invalidating old ones.
 */
export const REPORT_QUESTIONS_VERSION = 1;

export const REPORT_EXPECTATIONS = ['as-expected', 'partly', 'opposite', 'no-clear-result'] as const;
export const REPORT_NEXT_STEPS = ['ship-winner', 'iterate', 'retest', 'drop-idea', 'undecided'] as const;
export const REPORT_OUTSIDE_FACTORS = [
  'promotion',
  'seasonality',
  'traffic-change',
  'site-release',
  'tracking-issue',
  'other',
] as const;

/** Caps on the free text answers. Untrusted input that may reach a model later, so kept short. */
export const REPORT_TEXT_LIMITS = { explanation: 2000, outsideNote: 1000, learning: 2000 } as const;

/**
 * The parts of an experiment that record who first wrote them: the brief's four sections
 * and the debrief. Recorded once, when a section goes from empty to having content, and
 * never changed by later edits; see experiment_section_authors.
 */
export const EXPERIMENT_SECTIONS = ['hypothesis', 'description', 'pxl', 'mechanisms', 'debrief'] as const;

export type ExperimentSection = (typeof EXPERIMENT_SECTIONS)[number];

export const ANNOUNCEMENT_SEVERITIES = ['neutral', 'info', 'urgent'] as const;

// Only types something actually emits. A catalogue entry with no producer reads
// as a feature that exists and silently never fires.
export const NOTIFICATION_TYPES = [
  'experiment.started',
  'experiment.ended',
  'experiment.deployed',
  'experiment.significant.positive',
  'experiment.significant.negative',
  // Health, as opposed to lifecycle. These say an experiment cannot be trusted
  // or has stopped being an experiment, which is more urgent than any result it
  // is currently reporting.
  'experiment.srm.detected',
  'experiment.no_data',
  'experiment.overdue',
  // The scheduler wanted to act and would not. There is deliberately no
  // auto_started or auto_stopped counterpart: starting and stopping already emit
  // experiment.started and experiment.ended whoever triggered them, and a second
  // notification for the same event would only say the same thing twice. Who did
  // it is recorded in the activity log, which is where "who" belongs.
  'experiment.automation_blocked',
  'implementation.done',
  'user.invited',
  'website.created',
  // Superadmin-triggered, company-scoped billing actions (AdminTools > Billing).
  // Not a generic "billing state changed" catch-all: each is a specific action
  // an admin took, which is what the activity log needs to be useful.
  'billing.mode_changed',
  'billing.trial_extended',
  // Raised by the allowance check (src/crons/billing/check-allowance.ts) for
  // card accounts, to Owners and Admins only. The two upgrade types are the
  // notice that precedes a plan move (scheduled) and the one confirming a move on
  // an annual plan when it is made (upgraded).
  'billing.allowance_warning',
  'billing.allowance_reached',
  'billing.plan_upgrade_scheduled',
  'billing.plan_upgraded',
  // A website's server SDK key was created or revoked (src/servers/api/server-keys). Raised
  // to the account's Owners and Admins only: a key reads every server-decided experiment
  // and its salt, so an unexpected one is a security event, not a lifecycle note.
  'server_key.created',
  'server_key.revoked',
  // A feature flag's rollout moved without anyone pressing a button (P-PER-59): the scheduler
  // published it or turned it off as scheduled, advanced its plan or finished it, and the
  // reminder to build a long-lived editor flag into the site. Changes made by hand are in the
  // activity log only.
  'flag.published',
  'flag.rollout_advanced',
  'flag.rollout_done',
  'flag.turned_off',
  'flag.implement_reminder',
  // The KPI guardrail found On worse than Off by more than the margin and paused the plan
  // (PER-340). Pause plus notify only, never an automatic rollback (decided 2026-10-26).
  'flag.guardrail_breach',
] as const;

/**
 * Delivery channels. 'inApp' is listed so the settings screen can render it, but
 * it is never stored and never togglable: the feed is the record of what
 * happened, and an account that switched it off would be asking the product to
 * forget things rather than to stop interrupting them.
 */
export const NOTIFICATION_CHANNELS = ['inApp', 'email', 'slack'] as const;

/**
 * Applied when an account has no stored row for a type, which is the normal
 * state - rows are only written once someone changes something. Keeping the
 * default here rather than in a column default means changing product policy
 * later actually moves every account that never opened the screen.
 *
 * Health and results earn an interruption; lifecycle events are things the
 * person reading about them usually just did.
 */
export const DEFAULT_NOTIFICATION_CHANNELS: Record<string, { email: boolean; slack: boolean }> = {
  'experiment.started': { email: false, slack: true },
  'experiment.ended': { email: false, slack: true },
  'experiment.deployed': { email: false, slack: true },
  'experiment.significant.positive': { email: true, slack: true },
  'experiment.significant.negative': { email: true, slack: true },
  'experiment.srm.detected': { email: true, slack: true },
  'experiment.no_data': { email: true, slack: true },
  'experiment.overdue': { email: true, slack: true },
  'experiment.automation_blocked': { email: true, slack: true },
  'implementation.done': { email: false, slack: false },
  'user.invited': { email: false, slack: false },
  'website.created': { email: false, slack: false },
  'billing.mode_changed': { email: true, slack: true },
  'billing.trial_extended': { email: true, slack: false },
  'billing.allowance_warning': { email: true, slack: false },
  'billing.allowance_reached': { email: true, slack: true },
  'billing.plan_upgrade_scheduled': { email: true, slack: true },
  'billing.plan_upgraded': { email: true, slack: true },
  'server_key.created': { email: true, slack: true },
  'server_key.revoked': { email: true, slack: true },
  'flag.published': { email: true, slack: true },
  'flag.rollout_advanced': { email: false, slack: true },
  'flag.rollout_done': { email: false, slack: true },
  'flag.turned_off': { email: true, slack: true },
  'flag.implement_reminder': { email: true, slack: false },
  'flag.guardrail_breach': { email: true, slack: true },
};

/**
 * Grouping for the settings matrix. Health is deliberately separate from
 * lifecycle: those rows say an experiment cannot be trusted, which is a
 * different decision to make than "tell me when things start".
 */
export const NOTIFICATION_TYPE_GROUPS = [
  {
    key: 'experiment.lifecycle',
    label: 'Experiment lifecycle',
    description: 'Where your experiments are in their run.',
    types: ['experiment.started', 'experiment.ended', 'experiment.deployed'],
  },
  {
    key: 'experiment.results',
    label: 'Results',
    description: 'When an experiment reaches a conclusion.',
    types: ['experiment.significant.positive', 'experiment.significant.negative'],
  },
  {
    key: 'experiment.health',
    label: 'Health',
    description: 'When an experiment cannot be trusted or has stopped collecting.',
    types: ['experiment.srm.detected', 'experiment.no_data', 'experiment.overdue', 'experiment.automation_blocked'],
  },
  {
    key: 'account',
    label: 'Team and websites',
    description: 'Changes to your account rather than your experiments.',
    types: ['implementation.done', 'user.invited', 'website.created', 'server_key.created', 'server_key.revoked'],
  },
  {
    key: 'billing',
    label: 'Billing',
    description: 'Changes to your billing, and how much of your plan you are using.',
    types: [
      'billing.mode_changed',
      'billing.trial_extended',
      'billing.allowance_warning',
      'billing.allowance_reached',
      'billing.plan_upgrade_scheduled',
      'billing.plan_upgraded',
    ],
  },
  {
    key: 'flags',
    label: 'Feature flags',
    description:
      'When a scheduled rollout moves or a guardrail pauses it, and when a quick fix should be built into your site.',
    types: [
      'flag.published',
      'flag.rollout_advanced',
      'flag.rollout_done',
      'flag.turned_off',
      'flag.implement_reminder',
      'flag.guardrail_breach',
    ],
  },
] as const;

/**
 * Who reports a website's conversions. One reporter per website, never mixed.
 *
 * 'server'  conversions are reported only by the customer's backend, through the SDK or
 *           POST /sdk/events; the browser runtime reports nothing. The value exists from
 *           PER-132 so the enum lands with the schema; the behaviour arrives in later PRs
 *           (runtime PER-140, /sdk/events PER-141, dashboard PER-142), and until then the
 *           settings route refuses it.
 */
export const TRACKING_METHODS = ['ga4', 'matomo', 'pertento', 'server'] as const;

/**
 * What a website permits an event to be used as, not what any one experiment
 * measures. Several events can be primary-eligible; each experiment picks one.
 *
 * 'ignored' is the default for a newly discovered event: the tracking server
 * registers every event name it sees, so without an opt-in the mapping would
 * fill with whatever a site happens to emit.
 */
export const EVENT_GOAL_ROLES = ['primary', 'secondary', 'ignored'] as const;

export const URL_TARGETING_CONDITIONS = [
  'equals',
  'starts with',
  'contains',
  'ends with',
  'matches regex',
  'not equals',
  'does not contain',
] as const;

/**
 * One experiment's Targeting & Conditions card, frozen under a name (saved_targetings.rules).
 * Each list is one rule kind; an empty list means that kind is not restricted, and all four
 * empty is valid ("runs on all"). Rules carry no ids or timestamps from the experiment they
 * were read from.
 */
export type SavedTargetingRules = {
  url: { url: string; condition: (typeof URL_TARGETING_CONDITIONS)[number] }[];
  cookie: { cookieName: string; cookieValues: string[] }[];
  device: { device: (typeof DEVICE_TYPES)[number] }[];
  activation: { kind: (typeof ACTIVATION_KINDS)[number]; value: string }[];
};

/**
 * One experiment's Objectives card, frozen under a name (saved_objectives.rules). A copy of the
 * experiment's own columns, not the resolved objectives: a null primary stays null, meaning the
 * website's default, so loading it onto another experiment follows that website rather than
 * freezing today's event. Secondaries keep the operator's order.
 */
export type SavedObjectiveRules = {
  primaryGoal: string | null;
  secondaryGoals: string[];
};

/**
 * Every action an html change can be stored with. Not all are offered everywhere: the editor offers
 * `inner` only in an extension whose runtime previews it (editor app state/current-editor-runtime.ts).
 */
export const HTML_CODE_EDITOR_ACTIONS = ['replace', 'insert', 'append', 'before', 'after', 'inner'] as const;

export const TIMESPANS = ['alltime', 'lastweek', 'lastmonth', 'lastyear'] as const;

// Surveys
//
// The three scales are the three standard CRO instruments and differ only in their
// score range, which lives in SURVEY_SCORE_RANGES below rather than being hardcoded
// in the widget and the reporting queries separately.
export const SURVEY_SCORE_SCALES = ['NPS', 'CSAT', 'CES'] as const;

// A survey of one of the instrument types answers its score questions on that
// instrument's scale. A Custom survey has no instrument, so each of its score
// questions names its own scale in its config (see surveyScoreScale below).
export const SURVEY_TYPES = [...SURVEY_SCORE_SCALES, 'Custom'] as const;

// No 'Deployed'. A survey is never rolled out to all traffic the way a winning
// variant is, so reusing EXPERIMENT_STATUSES would put a dead value in the enum.
export const SURVEY_STATUSES = ['Draft', 'Running', 'Ended'] as const;

export const SURVEY_POSITIONS = ['bottom right', 'bottom left', 'bottom center', 'center'] as const;

// 'exit intent' resolves to two different detectors at runtime, chosen from the
// detected device: mouseleave above the viewport on desktop, and fast upward
// scrolling on touch. It is one trigger with two implementations, not two
// triggers — do not add a separate mobile value here.
export const SURVEY_TRIGGERS = ['page view', 'delay', 'exit intent', 'event', 'scroll depth'] as const;

export const SURVEY_QUESTION_KINDS = ['score', 'open text', 'single choice', 'multiple choice'] as const;

export const SURVEY_SENTIMENTS = ['positive', 'neutral', 'negative'] as const;

// Inclusive bounds per instrument. The widget builds its controls from these and
// the API validates submitted scores against them, so a range only ever changes
// in one place.
export const SURVEY_SCORE_RANGES = {
  NPS: { min: 0, max: 10 },
  CSAT: { min: 1, max: 5 },
  CES: { min: 1, max: 7 },
} as const;

export type SurveyScoreScale = (typeof SURVEY_SCORE_SCALES)[number];

// The widget's own copy when the customer has not written theirs (surveys.texts).
export const SURVEY_DEFAULT_TEXTS = {
  submit: 'Submit',
  next: 'Next',
  thankYou: 'Thank you for your feedback',
  closeLabel: 'Close survey',
} as const;

// A score question's end labels when the customer has not written their own. The
// bundle generator resolves them into the visitor bundle and the builder preview
// shows the same pair, so the two cannot disagree.
export const SURVEY_SCALE_END_LABELS: Record<SurveyScoreScale, readonly [string, string]> = {
  NPS: ['Not at all likely', 'Extremely likely'],
  CSAT: ['Very unsatisfied', 'Very satisfied'],
  CES: ['Very difficult', 'Very easy'],
};

type SurveyScoreScaleParams = { surveyType: (typeof SURVEY_TYPES)[number]; scale?: SurveyScoreScale };

/**
 * The scale a score question is answered on: the instrument's own for an NPS, CSAT or
 * CES survey (whatever the question says), the question's own for a Custom one.
 *
 * Null for a Custom score question that names no scale. The API never stores one, so
 * a caller seeing null is looking at bad data and should reject, not guess.
 */
export function surveyScoreScale({ surveyType, scale }: SurveyScoreScaleParams): SurveyScoreScale | null {
  if (surveyType !== 'Custom') return surveyType;
  return scale ?? null;
}

// Billing
//
// 'card' is the only mode a company can reach on its own, via Polar checkout.
// 'free' and 'invoice' are superadmin-only exits from the card flow: 'free' means
// nobody is charged and nothing is ever sent to Polar; 'invoice' means usage is
// still measured and rolled up, but billed externally rather than through Polar
// (Polar is a card-only Merchant of Record — it has no invoice/bank-transfer path).
export const BILLING_MODES = ['card', 'free', 'invoice'] as const;

// One Polar meter per feature. Usage is measured for every mode (including
// 'free') so cost-to-serve stays visible even where nobody is billed; billing_mode
// only decides what gets sent to Polar.
//
// This is a known-values list, not an enum source — billing_usage_hourly.feature
// and every table in the usage-metering pipeline (src/crons/billing/) are varchar,
// deliberately, so a new billable feature never needs a schema migration. Add a
// name here when something starts reporting it via recordUsage(); the pipeline
// itself needs no code change.
//
// `website_sessions` is the billable unit of the account plans: one per browser
// tab per website, whichever features ran in it. The other browser features are
// kept as internal per-feature counts for cost analysis, and are not billed.
export const BILLING_FEATURES = [
  'website_sessions',
  'experiment_sessions',
  'deployed_variant_sessions',
  'survey_engagement',
  'tokens',
] as const;

// Pertento's own company row. Usage generated by it, or by anything it manages
// directly (companies.under_pertento), is never billed — see isInternalTraffic
// in src/crons/billing/resolve-payer.ts. Here rather than inline so the id that
// decides whether a company is billed at all is not a bare literal in three
// files.
export const PERTENTO_COMPANY_ID = 1;

// The subset a browser is allowed to report over the public /usage beacon.
// `tokens` is deliberately absent: it is the most expensive planned unit and
// originates server-side only (a cron recording an Anthropic call's cost), so
// accepting it from an unauthenticated endpoint would let anyone mint the
// priciest feature's usage against any website. Server-side inserters write
// usage_events directly and are not bound by this list.
export const BROWSER_BILLING_FEATURES = [
  'website_sessions',
  'experiment_sessions',
  'deployed_variant_sessions',
  'survey_engagement',
] as const;

// The account plans, smallest first: the order is the upgrade path (an
// automatic move up goes to the next one). Every account (a standalone company or
// an agency's client) has one plan subscription of its own; an agency pays for its
// clients' plans on its own Polar customer and holds the agency fee besides.
// Allowances, AI credits and prices live in billing-plans.ts. Real enums: this codebase
// decides what a plan is, so the sets are closed.
export const BILLING_PLANS = ['start', 'grow', 'scale', 'business', 'unlimited'] as const;

// How often a plan is paid. Annual is the same plan at a discount, still with a
// monthly session allowance.
export const BILLING_INTERVALS = ['month', 'year'] as const;

// What a subscription is for: an account's plan (sessions and AI credits), or the
// agency fee (the agency features, no sessions and no credits).
export const BILLING_SUBSCRIPTION_KINDS = ['plan', 'agency_fee'] as const;

// How long a past_due subscription keeps serving after the first failed
// renewal charge. Polar retries for 21 days before revoking (see
// docs/billing/lifecycle.md); 7 days gives a customer with an expired card time
// to fix it without serving unpaid for the whole retry window.
export const PAST_DUE_GRACE_DAYS = 7;

// The one currency every figure is priced and shown in: euro. Polar's
// organization currency and every product price must be EUR too (see
// docs/billing/deploy.md).
export const BILLING_CURRENCY = 'EUR';

// Polar's own fixed vocabulary for subscription status, mirrored verbatim
// (polarsource/polar-js SubscriptionStatus) — not something this codebase
// extends, so also a real enum.
export const BILLING_SUBSCRIPTION_STATUSES = [
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'paused',
] as const;

// The login screen's chat. An announcement is a few messages the visitor can
// follow up on with suggested questions; each answer is either a text bubble or a
// link card. Limits live here so the API validation and the editor's maxLength
// cannot drift.
export const SIGNIN_REPLY_KINDS = ['text', 'link'] as const;

export const SIGNIN_ANNOUNCEMENT_LIMITS = {
  intro: 160,
  headline: 160,
  body: 1000,
  replies: 4,
  question: 80,
  answerText: 600,
  linkLabel: 64,
  linkUrl: 512,
} as const;

// How many announcements the login chat plays: the newest and the ones before it.
export const SIGNIN_HISTORY_LIMIT = 3;

export type SigninReply =
  | { question: string; kind: 'text'; text: string }
  | { question: string; kind: 'link'; label: string; url: string };

/**
 * Every product tour, by key. A tour's content lives with the dashboard; this list is what
 * the server accepts when a user finishes or skips one. It is not a PostgreSQL enum on
 * purpose: new features ship with a tour, and a tour should not need a `db:push` to land.
 */
export const TOUR_KEYS = [
  'platform',
  'home',
  'clients',
  'health',
  'performance',
  'program',
  'experiments.library',
  'experiments.settings',
  'experiments.brief',
  'experiments.results',
  'experiments.debrief',
  'pipelines',
  'pipelines.board',
  'flags.library',
  'flags.rollout',
  'flags.brief',
  'flags.targeting',
  'flags.setup',
  'flags.activity',
  'learnings',
  'learnings.setup',
  'learnings.waiting',
  'learnings.evidence',
  'learnings.opportunities',
  'learnings.pattern',
  'learnings.test',
  'combination',
  'implementation',
  'surveys.library',
  'surveys.builder',
  'surveys.overview',
  'surveys.results',
  'websites.list',
  'google-analytics',
  'settings.general',
  'settings.account',
  'settings.team',
  'settings.billing',
  'settings.agent-access',
] as const;

export type TourKey = (typeof TOUR_KEYS)[number];

/**
 * How a user left a tour. `suppressed` is a tour that had nothing left to show this user
 * (every step needed a capability they lack), recorded so it is not re-evaluated forever.
 */
export const TOUR_OUTCOMES = ['completed', 'skipped', 'suppressed'] as const;

export type TourOutcome = (typeof TOUR_OUTCOMES)[number];

/**
 * Data feeds (P-PER-41): which item of a feed a visitor gets. `first` for a feed with one
 * useful item for the whole site (a reviews summary); `match` for the item whose field
 * matches a value of the visitor's page (any data field: the address, a campaign, the
 * language, a product); `current` for the item whose start and end dates contain today (the
 * offer running now); `random` for any item, the same one for the whole visit.
 */
export const FEED_PICKS = ['first', 'match', 'current', 'random'] as const;

export type FeedPick = (typeof FEED_PICKS)[number];

/**
 * How a `match` feed compares: `equals` (ignoring case), `contains` (the page value contains
 * the item's, the longest wins: /hotels/stockholm-city contains "stockholm"), `path` (the
 * same page address, ignoring host, trailing slash and case).
 */
export const FEED_MATCH_MODES = ['equals', 'contains', 'path'] as const;

export type FeedMatchMode = (typeof FEED_MATCH_MODES)[number];

/** How long a fetched feed is served before the backend fetches it again, in seconds. */
export const FEED_REFRESH_SECONDS = [300, 900, 3600, 21600, 86400, 604800] as const;

export type FeedRefreshSeconds = (typeof FEED_REFRESH_SECONDS)[number];
export * from './editor-ai';
export * from './results-ai';
export * from './change-sources';
export * from './activity-actors';
export * from './learnings';
export * from './learnings-ask';
export * from './feature-flags';
export * from './beta-features';
